import type { FoodInput, MealType } from '@fitness/api-client';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { FoodForm } from '@/components/food/FoodForm';
import { newFoodSchema, type NewFoodSearch } from '@/components/food/searchParams';
import { todayIso } from '@/components/food/format';
import { describeError } from '@/lib/api';
import { useCreateFood } from '@/lib/queries/foods';

export const Route = createFileRoute('/_app/food/foods/new')({
  validateSearch: (search: Record<string, unknown>): NewFoodSearch => newFoodSchema.parse(search),
  component: NewFoodPage,
});

/**
 * Alta de un alimento propio.
 *
 * Se llega aquí desde el escáner cuando el código no lo conoce nadie —y
 * entonces `barcode` viene en la URL— o desde una búsqueda sin resultados.
 * Creado el alimento, se sigue el camino natural: su ficha, con «Añadir al
 * diario» a un toque.
 */
function NewFoodPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const date = search.date ?? todayIso();
  const meal: MealType = search.meal ?? 'lunch';
  const create = useCreateFood();

  function handleSubmit(input: FoodInput): void {
    create.mutate(input, {
      onSuccess: (food) => {
        void navigate({ to: '/food/foods/$id', params: { id: food.id }, search: { date, meal } });
      },
    });
  }

  return (
    <>
      <PageHeader
        title="Nuevo alimento"
        subtitle="Se guarda solo para ti y podrás usarlo en el diario y en tus recetas."
        action={
          <Link
            to="/food/add"
            search={{ date, meal }}
            aria-label="Volver"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <FoodForm
        barcode={search.barcode}
        onSubmit={handleSubmit}
        submitting={create.isPending}
        errorTitle="No se ha podido crear el alimento"
        errorMessage={create.isError ? describeError(create.error, { saving: true }) : undefined}
        submitLabel="Crear alimento"
        onCancel={() => void navigate({ to: '/food/add', search: { date, meal } })}
      />
    </>
  );
}
